import { loadStripe } from '@stripe/stripe-js';
import type { CheckoutMethod, ServiceModel, ServiceTier } from '../types/portfolio';

export type PaymentRequest = {
  tier: ServiceTier;
  model?: ServiceModel;
  method: CheckoutMethod;
  orderId: string;
  customerEmail?: string;
};

export type PaymentResult = {
  orderId: string;
  invoiceId: string;
  status: 'payment_ready' | 'success' | 'error';
  message: string;
  redirectUrl?: string;
};

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripeCheckoutUrl = import.meta.env.VITE_STRIPE_CHECKOUT_URL;
const stripeTierLinks: Partial<Record<ServiceTier['id'], string>> = {
  site: import.meta.env.VITE_STRIPE_LINK_SITE,
  program: import.meta.env.VITE_STRIPE_LINK_PROGRAM,
  game: import.meta.env.VITE_STRIPE_LINK_GAME,
  state: import.meta.env.VITE_STRIPE_LINK_STATE,
};
const stripeModelLinks: Record<string, string | undefined> = {
  'site-simple': import.meta.env.VITE_STRIPE_LINK_SITE_SIMPLE,
  'site-intermediate': import.meta.env.VITE_STRIPE_LINK_SITE_INTERMEDIATE,
  'site-premium': import.meta.env.VITE_STRIPE_LINK_SITE_PREMIUM,
  'program-simple-tool': import.meta.env.VITE_STRIPE_LINK_PROGRAM_SIMPLE_TOOL,
  'program-average-desktop': import.meta.env.VITE_STRIPE_LINK_PROGRAM_AVERAGE_DESKTOP,
  'program-professional': import.meta.env.VITE_STRIPE_LINK_PROGRAM_PROFESSIONAL,
  'game-small-2d': import.meta.env.VITE_STRIPE_LINK_GAME_SMALL_2D,
  'game-unity-3d': import.meta.env.VITE_STRIPE_LINK_GAME_UNITY_3D,
  'game-mobile': import.meta.env.VITE_STRIPE_LINK_GAME_MOBILE,
  'game-ultimate': import.meta.env.VITE_STRIPE_LINK_GAME_ULTIMATE,
  'state-simple': import.meta.env.VITE_STRIPE_LINK_STATE_SIMPLE,
  'state-medium': import.meta.env.VITE_STRIPE_LINK_STATE_MEDIUM,
  'state-dashboard': import.meta.env.VITE_STRIPE_LINK_STATE_DASHBOARD,
};

function isDirectPaymentLink(url: string) {
  return /^https:\/\/(buy\.stripe\.com|checkout\.stripe\.com)\//.test(url);
}

function appendStripeTrackingParams(url: string, orderId: string, email?: string) {
  const nextUrl = new URL(url);
  nextUrl.searchParams.set('client_reference_id', orderId.replace(/[^\w-]/g, '_').slice(0, 200));
  if (email) {
    nextUrl.searchParams.set('prefilled_email', email);
  }
  return nextUrl.toString();
}

function createInvoiceId() {
  return `INV-${new Date().getFullYear()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

function createOrderId() {
  return `PAY-${Date.now().toString(36).toUpperCase()}`;
}

async function createStripeCheckout(request: PaymentRequest): Promise<PaymentResult> {
  const orderId = request.orderId || createOrderId();
  const invoiceId = createInvoiceId();
  const checkoutUrl = (request.model ? stripeModelLinks[request.model.id] : undefined) || stripeTierLinks[request.tier.id] || stripeCheckoutUrl;

  if (!checkoutUrl) {
    return {
      orderId,
      invoiceId,
      status: 'error',
      message: 'No Stripe payment link is configured for this model.',
    };
  }

  if (isDirectPaymentLink(checkoutUrl)) {
    return {
      orderId,
      invoiceId,
      status: 'payment_ready',
      message: 'Payment link is ready. The order is saved as awaiting payment; verify the payment in Stripe by Order ID or email.',
      redirectUrl: appendStripeTrackingParams(checkoutUrl, orderId, request.customerEmail),
    };
  }

  if (!stripePublishableKey) {
    return {
      orderId,
      invoiceId,
      status: 'error',
      message: 'Stripe publishable key is missing.',
    };
  }

  try {
    const stripe = await loadStripe(stripePublishableKey);
    const response = await fetch(checkoutUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderId,
        tierId: request.tier.id,
        tierName: request.tier.name,
        modelId: request.model?.id,
        modelName: request.model?.name,
        modelPrice: request.model?.price,
        customerEmail: request.customerEmail,
      }),
    });

    if (!response.ok) throw new Error('Stripe checkout failed.');
    const session = (await response.json()) as { id?: string; url?: string };

    if (session.id && stripe) {
      await stripe.redirectToCheckout({ sessionId: session.id });
    }

    return {
      orderId,
      invoiceId,
      status: 'payment_ready',
      message: 'Stripe checkout session created.',
      redirectUrl: session.url,
    };
  } catch (error) {
    console.warn('Stripe checkout endpoint is not ready; using demo mode.', error);
    return {
      orderId,
      invoiceId,
      status: 'error',
      message: 'Stripe checkout endpoint is not ready.',
    };
  }
}

export async function startPayment(request: PaymentRequest): Promise<PaymentResult> {
  return createStripeCheckout(request);
}
